<script>
	import _ from 'underscore';
	import assignmentService from 'services/assignmentService';
	import { studentCompleteOfflineVocabActivity } from 'utilities/offlineUtilities';
	import pubsub from 'events/pubsub';

	const {
		fireEvent,
		events: { VOCAB_MATCHER_AUDIO_PLAY, VOCAB_MATCHER_AUDIO_PAUSE, VOCAB_MATCHER_AUDIO_COMPLETE, VOCAB_ACTIVITY_COMPLETE, VOCAB_ACTIVITY_RESET }
	} = require('utilities/analyticsUtilities');

	const TOTAL_LAST_TOUCHED_SYNONYM_PADDING = 20;

	// bubble sizes
	const RADIUS = 200;

	const BUBBLE_PLAYER_X_OFFSET = -35;
	const BUBBLE_PLAYER_Y_OFFSET = 40;

	const VIEWBOX_WIDTH = 3200;
	const VIEWBOX_HEIGHT = 1200;

	// the horizontal and vertical margin between the edge of the synonym and the border around it
	const SYNONYM_MARGIN_X = 15;
	const SYNONYM_MARGIN_Y = 10;

	// how far apart should synonyms connected to the same bubble be?
	const CONNECTED_SYNONYM_MARGIN = 20;

	// How far (from center of bubble) does the attraction/repulsion reach?
	const GRAVITY_MAX_RANGE = RADIUS;

	// How far bubble's attraction and repulsion reaches while bubble is being moved
	const GRAVITY_MAX_RANGE_MOUSEDOWN = 350;

	// if distance <1 then it effectively multiplies force instead of dividing it
	// we don't need to model a singularity
	const MIN_DIST = RADIUS / 2;

	// the strength of the attraction/repulsion effect
	const GRAVITY_CONSTANT = 23000;

	// how fast should friction slow the inertia of moving objects?
	const COEFFICIENT_OF_FRICTION = 0.8;

	// velocity at which we just set it to 0
	const MIN_VELOCITY = 0.0001;

	// terminal velocity for target words
	const MAX_VELOCITY = 1000;

	const BOUNCE_DENOMENATOR = RADIUS * 6;

	// how close do targets have to get to their match before they're connected
	const CONNECTION_RANGE = RADIUS * 1.4;

	// if false, students can use the wrong word to push a word into its match and a connection will form
	// if true, connections only form with words the student is currently moving
	const ONLY_CONNECT_MOVING_BUBBLE = true;

	// how long in seconds should it take for a connecting line to be drawn once
	// a target is connected to its match
	const CONNECTION_DURATION = 0.2;

	const OVERLAPPING_CONNECTION_RADIANS_PER_SEC = Math.PI / 10;

	// const CORD_UNTENSIONED_LENGTH = RADIUS * 2.6;

	// // how fast should friction slow the inertia of a CONNECTED word
	// const CORD_COEFFICIENT_OF_FRICTION = .99;

	// const CORD_STRENGTH = 2000;

	function eventPosition(ev) {
		let evPos;
		if (ev.type.substr(0, 5) === 'touch') {
			evPos = ev.originalEvent ? ev.originalEvent.touches[0] : ev.touches[0];
		} else {
			evPos = ev.originalEvent || ev;
		}

		return typeof evPos.clientX === 'number' ? evPos : null;
	}

	function distance(p1, p2) {
		return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
	}

	function directionFromXYVector(x, y) {
		let direction = Math.atan2(y, x);
		while (direction < 0) {
			direction += Math.PI * 2;
		}
		return direction % (Math.PI * 2);
	}

	/**
	 * turns an xVel/yVel on p into an overal velocity value, so we
	 * can check this against limits
	 */
	function velocityFromXYVel(p) {
		return Math.sqrt(Math.pow(p.xVel, 2) + Math.pow(p.yVel, 2));
	}

	/**
	 * given el as the element where the line starts and x1, y1 within that element's bbox
	 * return an {x, y} object as a point to start the line just at the edge of the bbox
	 */
	function getLineStartOutsideBBox(el, x1, y1, x2, y2) {
		const bbox = el.getBBox();
		if (x1 === x2) {
			// vertical line
			return {
				x: x1,
				y: y1 > y2 ? bbox.y : bbox.y + bbox.height
			};
		} else if (y1 === y2) {
			// horizontal line
			return {
				x: x1 > x2 ? bbox.x : bbox.x + bbox.width,
				y: y1
			};
		} else {
			// using basic straight line equation: y = mx + b
			// we just need to find m & b
			const m = (y2 - y1) / (x2 - x1);
			const b = y1 - m * x1;
			let x, y;

			// try using the top or bottom of the bbox, find where our line
			// would intersect top or bottom depending on which direction we're going
			y = y2 > y1 ? bbox.y + bbox.height : bbox.y;
			x = (y - b) / m;

			// if the intersection is within the bbox's bounds, we're done
			if (x >= bbox.x && x <= bbox.x + bbox.width) {
				return { x, y };
			}

			// try using the left or right of the bbox, find where our line
			// would intersect left or right depending on which direction we're going
			x = x2 > x1 ? bbox.x + bbox.width : bbox.x;
			y = m * x + b;

			// if the intersection is within the bbox's bounds, we're done
			if (y >= bbox.y && y <= bbox.y + bbox.height) {
				return { x, y };
			}

			// found no intersection with bbox, default to original point
			return {
				x: x1,
				y: y1
			};
		}
	}

	function handleOverlappingConnectedSynonyms(elapsed, bubble, synonyms) {
		const connected = synonyms.filter(syn => syn.matchingBubble === bubble && syn.connectedAt);

		connected.forEach(syn => {
			let dist = distance({ x: 0, y: 0 }, { x: syn.connectX, y: syn.connectY });
			const angle = directionFromXYVector(syn.connectX, syn.connectY);

			if (dist < RADIUS * 1.5) {
				dist += RADIUS * elapsed;
				syn.connectX = Math.cos(angle) * dist;
				syn.connectY = Math.sin(angle) * dist;
			}

			const synBBox = document.getElementById('matcher-synonym-bg-' + syn.idx).getBBox();
			let collidedWith = null;
			connected.forEach(other => {
				if (other.idx === syn.idx || other.connectedAt < syn.connectedAt) {
					return;
				}

				// check for overlap
				const otherBBox = document.getElementById('matcher-synonym-bg-' + other.idx).getBBox();

				// other is too far right
				if (otherBBox.x - CONNECTED_SYNONYM_MARGIN > synBBox.x + synBBox.width) {
					// console.log('other is too far right');
					return;
				}

				// other is too far left
				if (otherBBox.x + otherBBox.width < synBBox.x - CONNECTED_SYNONYM_MARGIN) {
					// console.log('other is too far left');
					return;
				}

				// other is too low
				if (otherBBox.y - CONNECTED_SYNONYM_MARGIN > synBBox.y + synBBox.height) {
					// console.log('other is too low');
					return;
				}

				// other is too high
				if (otherBBox.y + otherBBox.height < synBBox.y - CONNECTED_SYNONYM_MARGIN) {
					// console.log('other is too high');
					return;
				}

				collidedWith = other;
			});

			if (collidedWith) {
				const otherAngle = directionFromXYVector(collidedWith.connectX, collidedWith.connectY);
				let angleDelta = elapsed * OVERLAPPING_CONNECTION_RADIANS_PER_SEC;
				if (otherAngle > angle) {
					angleDelta *= -1;
				}

				syn.connectX = Math.cos(angle + angleDelta) * dist;
				syn.connectY = Math.sin(angle + angleDelta) * dist;
			}
		});
	}

	function handleBubbleToEdgeCollisions(elapsed, bubble, el, synonyms) {
		const bbox = el.getBBox();

		let minX = bbox.x;
		let minY = bbox.y;
		let maxX = bbox.x + bbox.width;
		let maxY = bbox.y + bbox.height;

		// if synonynm connected, it should also bump against the edge
		synonyms.forEach(syn => {
			if (syn.matchingBubble !== bubble || !syn.connectedAt) {
				return;
			}
			const synEl = document.getElementById('matcher-synonym-bg-' + syn.idx);

			if (!synEl) {
				const synBBox = synEl.getBBox();

				if (synBBox.y < minY) {
					minY = synBBox.y;
				}

				if (synBBox.x < minX) {
					minX = synBBox.x;
				}

				if (synBBox.y + synBBox.height > maxY) {
					maxY = synBBox.y + synBBox.height;
				}

				if (synBBox.x + synBBox.width > maxX) {
					maxX = synBBox.x + synBBox.width;
				}
			}
		});

		if (maxX + bubble.xVel * elapsed >= VIEWBOX_WIDTH) {
			bubble.xVel = (VIEWBOX_WIDTH - maxX) / elapsed;
		}

		if (maxY + bubble.yVel * elapsed >= VIEWBOX_HEIGHT) {
			bubble.yVel = (VIEWBOX_HEIGHT - maxY) / elapsed;
		}

		if (minX + bubble.xVel * elapsed < 0) {
			bubble.xVel = (-1 * minX) / elapsed;
		}

		if (minY + bubble.yVel * elapsed < 0) {
			bubble.yVel = (-1 * minY) / elapsed;
		}
	}

	function angleDifference(a1, a2) {
		let diff = Math.abs(a1 - a2);
		// if we're more than 180 degrees, then go the other way around the circle
		// there should be an acute angle not an obtuse one
		if (diff > Math.PI) {
			diff += Math.PI * 2;
		}
		return diff % (Math.PI * 2);
	}

	function getBBoxCorners(bbox, addTopBottomMidpoints, refPoint) {
		/*
		 *
		 * we sometimes get distance for all corners to find the closest one
		 * if refPoint is provided we can skip a lot of distance calculations
		 *
		 * QUADRANTS ARE BASED ON THE TOP LEFT CORNER
		 * e.g. if the refPoint is in quadrant 1 we don't need to check c2, c3 or c3.5
		 *
		 * quadrant 1                                         quadrant 2
		 * (x less, y less)                                   (x greater, y less)
		 *                    x,y +-----------------+-----------------+
		 *                        |c1              c1.5             c2|
		 *                        |                                   |
		 *                        |           bounding box            |
		 *                        |                                   |
		 *                        |c4              c3.5             c3|
		 *                        +-----------------+-----------------+
		 *
		 * quadrant 4                                         quadrant 3
		 * (x less, y greater)                                (x greater, y greater)
		 */

		const corners = {
			c1: { x: bbox.x, y: bbox.y },
			c2: { x: bbox.x + bbox.width, y: bbox.y },
			c3: { x: bbox.x + bbox.width, y: bbox.y + bbox.height },
			c4: { x: bbox.x, y: bbox.y + bbox.height },

			'c1.5': { x: bbox.x + bbox.width / 2, y: bbox.y },
			'c3.5': { x: bbox.x + bbox.width / 2, y: bbox.y + bbox.height }
		};

		if (refPoint) {
			if (refPoint.x <= bbox.x && refPoint.y <= bbox.y) {
				// quadrant 1
				return [corners.c1];
			} else if (refPoint.x > bbox.x && refPoint.y <= bbox.y) {
				// quadrant 2
				return addTopBottomMidpoints ? [corners.c1, corners.c2, corners['c1.5']] : [corners.c1, corners.c2];
			} else if (refPoint.x > bbox.x && refPoint.y > bbox.y) {
				// quadrant 3
				return addTopBottomMidpoints ? [corners.c2, corners.c3, corners.c4, corners['c3.5']] : [corners.c2, corners.c3, corners.c4];
			} else {
				// quadrant 4
				return [corners.c1, corners.c4];
			}
		} else {
			if (addTopBottomMidpoints) {
				return [corners.c1, corners.c2, corners.c3, corners.c4, corners['c1.5'], corners['c3.5']];
			}
			return [corners.c1, corners.c2, corners.c3, corners.c4];
		}
	}

	function pointClosestTo(refPoint, otherPoints) {
		let nearest = VIEWBOX_WIDTH * 100;
		let closestPoint = null;
		for (let ci = 0; ci < otherPoints.length; ci++) {
			const dist = distance(otherPoints[ci], refPoint);
			if (dist < nearest) {
				nearest = dist;
				closestPoint = otherPoints[ci];
			}
		}

		return { distance: nearest, point: closestPoint };
	}

	function handleBubbleCollisions(elapsed, bubble, allBubbles, synonyms) {
		// need a quick simple test to eliminate a bubble that we just could never collide with

		// divide distance traveled into steps based on 0.9x the radius of a bubble
		// this way very high velocities won't skip over a bubble and fail to register collision
		const vel = velocityFromXYVel(bubble);
		const direction = directionFromXYVector(bubble.xVel, bubble.yVel);
		const connectedSynonyms = synonyms.filter(s => s.connectedAt && s.matchingBubble === bubble);
		let maxStep = RADIUS;

		if (connectedSynonyms.length) {
			const firstEl = document.getElementById('matcher-synonym-bg-' + connectedSynonyms[0].idx);
			if (firstEl) {
				maxStep = firstEl.getBBox().height * 0.9;
			}
		}

		const otherSynonyms = synonyms.filter(s => !s.connectedAt || s.matchingBubble !== bubble);

		let bubbleCollisionNormal = null;
		let collidedRef = null;
		let collidedWithRef = null;

		allBubbles.forEach(otherBubble => {
			if (otherBubble === bubble || bubbleCollisionNormal !== null) {
				// don't collide the bubble with itself, or stop if we have a collision
				return;
			}

			let stepIncrement = vel * elapsed > maxStep ? 1 / ((vel * elapsed) / maxStep) : 1;
			for (let step = 0; step <= 1 && bubbleCollisionNormal === null; step += stepIncrement) {
				const xInc = bubble.xVel * elapsed * step;
				const yInc = bubble.yVel * elapsed * step;
				collidedRef = bubble;
				collidedWithRef = otherBubble;

				const pos = {
					x: bubble.x + xInc,
					y: bubble.y + yInc
				};
				const distAtStep = distance(pos, otherBubble);
				if (distAtStep <= RADIUS * 2) {
					// draw a line along the collision... we do this by taking the direction
					// from one bubble's center to the other, adding 90 degrees, then do a modulo
					// to 360 (but we're using radians)
					bubbleCollisionNormal = (directionFromXYVector(pos.x - otherBubble.x, pos.y - otherBubble.y) + Math.PI / 2) % (Math.PI * 2);
					break;
				}
			}
		});

		// check for synonym to synonym collisions
		if (bubbleCollisionNormal === null) {
			connectedSynonyms.forEach(syn => {
				if (bubbleCollisionNormal !== null) {
					return;
				}
				const synEl = document.getElementById('matcher-synonym-bg-' + syn.idx);
				if (!synEl) {
					return;
				}
				collidedRef = syn;

				const synBBox = synEl.getBBox();

				otherSynonyms.forEach(otherSyn => {
					if (bubbleCollisionNormal !== null) {
						return;
					}

					collidedWithRef = otherSyn;
					const otherSynBBox = document.getElementById('matcher-synonym-bg-' + otherSyn.idx).getBBox();

					// check for horizontal collisions
					// check for collision from left to right
					if (synBBox.x + synBBox.width < otherSynBBox.x) {
						const points = [
							{ x: synBBox.x + synBBox.width, y: synBBox.y },
							{ x: synBBox.x + synBBox.width, y: synBBox.y + synBBox.height }
						];

						points.forEach(p => {
							if (p.y >= otherSynBBox.y && p.y <= otherSynBBox.y + otherSynBBox.height && p.x >= otherSynBBox.x) {
								bubbleCollisionNormal = Math.PI / 2;
							}
						});
					} else {
						// check for collision from right to left
						const points = [
							{ x: synBBox.x, y: synBBox.y },
							{ x: synBBox.x, y: synBBox.y + synBBox.height }
						];

						points.forEach(p => {
							if (p.y >= otherSynBBox.y && p.y <= otherSynBBox.y + otherSynBBox.height && p.x <= otherSynBBox.x + otherSynBBox.width) {
								bubbleCollisionNormal = Math.PI / 2;
							}
						});
					}

					// check for vertical collisions
					// check for collision from top moving down
					if (synBBox.y < otherSynBBox.y) {
						const points = [
							{ x: synBBox.x, y: synBBox.y + synBBox.height },
							{ x: synBBox.x + synBBox.width, y: synBBox.y + synBBox.height },
							{ x: synBBox.x + synBBox.width / 2, y: synBBox.y + synBBox.height }
						];

						points.forEach(p => {
							if (p.x >= otherSynBBox.x && p.x <= otherSynBBox.x + otherSynBBox.width && p.y >= otherSynBBox.y) {
								bubbleCollisionNormal = 0;
							}
						});
					} else {
						// check for collision from bottom moving up
						const points = [
							{ x: synBBox.x, y: synBBox.y },
							{ x: synBBox.x + synBBox.width, y: synBBox.y },
							{ x: synBBox.x + synBBox.width / 2, y: synBBox.y }
						];

						points.forEach(p => {
							if (p.x >= otherSynBBox.x && p.x <= otherSynBBox.x + otherSynBBox.width && p.y <= otherSynBBox.y + otherSynBBox.height) {
								bubbleCollisionNormal = 0;
							}
						});
					}
				});
			});
		}

		if (bubbleCollisionNormal !== null) {
			// we will collide, bounce the opposite direction

			const turn = angleDifference(bubbleCollisionNormal, direction) * 2;
			const nextPoints = [
				{
					xVel: Math.cos(direction + turn * 2) * vel,
					yVel: Math.sin(direction + turn * 2) * vel
				},
				{
					xVel: Math.cos(direction - turn * 2) * vel,
					yVel: Math.sin(direction - turn * 2) * vel
				}
			];
			nextPoints[0].x = collidedRef.x + nextPoints[0].xVel;
			nextPoints[0].y = collidedRef.y + nextPoints[0].yVel;
			nextPoints[1].x = collidedRef.x + nextPoints[1].xVel;
			nextPoints[1].y = collidedRef.y + nextPoints[1].yVel;
			const closest = pointClosestTo(collidedWithRef, nextPoints);
			if (closest.point === nextPoints[0]) {
				// 0 was closer, use 1
				bubble.xVel = nextPoints[1].xVel;
				bubble.yVel = nextPoints[1].yVel;
			} else {
				// 1 was closer, use 0
				bubble.xVel = nextPoints[0].xVel;
				bubble.yVel = nextPoints[0].yVel;
			}
		}
	}

	export default {
		components: {
			'audio-player-simple': require('components/audio-player-simple.vue').default,
			'text-to-speech-player-simple': require('components/text-to-speech-player-simple.vue').default,
			'vocab-powered-by-wordsmyth': require('components/vocab-powered-by-wordsmyth.vue').default
		},
		props: ['wordList', 'wordMatcher', 'assignmentId', 'product', 'selectedProduct', 'activityState', 'showTts'],
		modelData: ['offlineMode', 'printing'],
		data() {
			return {
				VIEWBOX_WIDTH,
				VIEWBOX_HEIGHT,
				BUBBLE_PLAYER_X_OFFSET,
				BUBBLE_PLAYER_Y_OFFSET,

				lastSynonymTouched: null,
				lastSynonymTouchedWidth: '2rem',
				stillAnimating: true,
				synonymMoving: null,
				// When movement begins track the starting position of the finger or cursor
				// so we know what the overall x/y diffs are
				movementStart: {
					x: 0,
					y: 0
				},
				// When movement begins track the starting position of the synonym
				// we use this to move the synonym based on the overall movement in movementStart
				synonymStart: {
					x: 0,
					y: 0
				},
				wordBubbles: [],
				synonyms: [],
				events: {
					VOCAB_MATCHER_AUDIO_PLAY,
					VOCAB_MATCHER_AUDIO_PAUSE,
					VOCAB_MATCHER_AUDIO_COMPLETE
				}
			};
		},
		created() {
			this.docMouseupHandler = ev => {
				if (this.synonymMoving) {
					ev.preventDefault();
					this.synonymMoving = null;
				}
			};
			this.docMousemoveHandler = ev => {
				if (this.synonymMoving) {
					ev.preventDefault();
					this.moveSynonym(ev);
				}
			};

			$(document).on('mouseup', this.docMouseupHandler);
			$(document).on('mousemove', this.docMousemoveHandler);
			$(document).on('touchend', this.docMouseupHandler);
			$(document).on('touchmove', this.docMousemoveHandler);
		},
		mounted() {
			let lastOffset = null;
			const runFrame = timeOffset => {
				if (!this.stillAnimating) {
					return;
				}

				// convert to seconds
				timeOffset /= 1000;
				lastOffset = lastOffset || timeOffset - 0.02;
				const elapsed = timeOffset - lastOffset;
				if (!this.complete) {
					this.makeConnections(elapsed, timeOffset);
					this.updateVelocities(elapsed);
					this.moveAndHandleCollisions(elapsed);
				}
				lastOffset = timeOffset;
				window.requestAnimationFrame(runFrame);
			};

			this.reset(false);
			window.requestAnimationFrame(runFrame);
			if (this.activityState && this.activityState.wm) {
				this.completeNow();
			}
		},
		beforeDestroy() {
			$(document).off('mouseup', this.docMouseupHandler);
			$(document).off('mousemove', this.docMousemoveHandler);
			$(document).off('touchend', this.docMouseupHandler);
			$(document).off('touchmove', this.docMousemoveHandler);
			this.stillAnimating = false;
		},
		computed: {
			complete() {
				return this.synonyms.every(s => !!s.connectedAt);
			},
			summaryEntries() {
				const entries = [];
				this.wordList.forEach(w => {
					if (this.wordMatcher[w]) {
						entries.push({
							word: w,
							synonyms: this.wordMatcher[w]
						});
					}
				});
				return entries;
			},
			synonymPrintWidth() {
				const rowCount = Math.ceil(this.synonyms.length / 2);

				return `${100 / rowCount}%`;
			},
			activeProduct() {
				return this.selectedProduct || this.product;
			},
			eventData() {
				return {
					aid: this.assignmentId,
					type: 'wm'
				};
			}
		},
		watch: {
			lastSynonymTouched() {
				this.$nextTick(() => {
					const el = this.$refs.lastSynonymTouched;
					if (el) {
						this.lastSynonymTouchedWidth = '' + (el.offsetWidth + TOTAL_LAST_TOUCHED_SYNONYM_PADDING) + 'px';
					}
				});
			},
			complete(c) {
				const data = this.eventData;
				if (c && this.assignmentId) {
					fireEvent(VOCAB_ACTIVITY_COMPLETE, data);
				}
				if (c && this.activityState && this.assignmentId && !this.activityState.wm) {
					let prms = null;
					if (this.offlineMode) {
						prms = studentCompleteOfflineVocabActivity(data);
					} else {
						prms = assignmentService.completeVocabActivity(data);
					}
					prms.then(() => {
						pubsub.publish('vocab-activity-complete', { wm: true });
					});
				}
			},
			printing(isPrinting, wasPrinting) {
				if (wasPrinting && !isPrinting) {
					this.reset(false);
				}
			}
		},
		methods: {
			completeNow() {
				this.synonyms = this.synonyms.map(s => {
					return Object.assign({}, s, { connectedAt: 1 });
				});
			},
			reset(emitEvent = true) {
				const bubbles = [];
				const synonyms = [];
				this.wordList.forEach(w => {
					if (this.wordMatcher[w]) {
						bubbles.push({
							x: 800,
							y: VIEWBOX_HEIGHT / 2,
							r: RADIUS,
							word: w,
							active: false,
							mass: 130 * w.length,
							xVel: 0,
							yVel: 0
						});

						this.wordMatcher[w].forEach((targetWord, idx) => {
							synonyms.push({
								matchingWord: w,
								matchingBubble: bubbles[bubbles.length - 1],
								word: targetWord,
								x: 0,
								y: 0,
								mass: 100 * w.length,
								connectedAt: 0,
								// when connection finishes, set the X & Y relative
								// to the bubble position and retain that position
								connectX: null,
								connectY: null
							});
						});
					}
				});

				synonyms.forEach((syn, idx) => {
					syn.idx = idx;
				});
				bubbles.forEach((b, idx) => {
					b.idx = idx;
				});

				// distribute evenly on X, random-ish Y
				const xColWidth = VIEWBOX_WIDTH / bubbles.length;
				const goodWordPositions = [];

				bubbles.forEach((bubble, idx) => {
					bubble.y = VIEWBOX_HEIGHT / 2;
					bubble.x = idx * xColWidth + xColWidth / 2;
					if (bubbles.length === 1) {
						goodWordPositions.push({
							x: bubble.x - xColWidth / 4,
							y: bubble.y * 0.2
						});
					}
					goodWordPositions.push({
						x: bubble.x,
						y: bubble.y * 0.2
					});
					goodWordPositions.push({
						x: bubble.x,
						y: bubble.y * 1.8
					});
					if (idx < bubbles.length - 1) {
						goodWordPositions.push({
							x: bubble.x + xColWidth / 2,
							y: bubble.y * 0.2
						});
						goodWordPositions.push({
							x: bubble.x + xColWidth / 2,
							y: bubble.y * 1.8
						});
						goodWordPositions.push({
							x: bubble.x + xColWidth / 2,
							y: bubble.y * 0.75
						});
						goodWordPositions.push({
							x: bubble.x + xColWidth / 2,
							y: bubble.y * 1.25
						});
					}
				});

				const randomizePosition = pos => {
					pos.x += Math.random() * (VIEWBOX_WIDTH * 0.06) - VIEWBOX_WIDTH * 0.03;
					pos.y += Math.random() * (VIEWBOX_HEIGHT * 0.1) - VIEWBOX_HEIGHT * 0.05;
				};
				goodWordPositions.forEach(randomizePosition);

				const shuffledPositions = _.shuffle(goodWordPositions);
				synonyms.forEach(synonym => {
					const pos = shuffledPositions.pop();
					if (pos) {
						synonym.x = pos.x;
						synonym.y = pos.y;
					} else {
						randomizePosition(synonym);
					}
				});

				this.wordBubbles = bubbles;
				this.synonyms = synonyms;

				if (emitEvent) {
					fireEvent(VOCAB_ACTIVITY_RESET, this.eventData);
				}

				this.$nextTick(() => {
					if (this.$el) {
						this.positionSynonymBackgrounds();
						this.$el.querySelectorAll('path.connector').forEach(connector => {
							connector.setAttribute('d', '');
						});
					}
				});
			},
			positionSynonymBackgrounds(synonym) {
				const move = syn => {
					const wordEl = document.getElementById('matcher-synonym-word-' + syn.idx);
					const bgEl = document.getElementById('matcher-synonym-bg-' + syn.idx);
					if (!wordEl || !bgEl) {
						return;
					}
					const bbox = wordEl.getBBox();
					bgEl.setAttribute('x', bbox.x - SYNONYM_MARGIN_X);
					bgEl.setAttribute('y', bbox.y - SYNONYM_MARGIN_Y);
					bgEl.setAttribute('width', bbox.width + SYNONYM_MARGIN_X * 2);
					bgEl.setAttribute('height', bbox.height + SYNONYM_MARGIN_Y * 2);
				};
				if (synonym) {
					move(synonym);
				} else {
					this.synonyms.forEach(move);
				}
			},
			makeConnections(elapsed, timeOffset) {
				// (re)draw connection of any matching words
				this.synonyms.forEach(syn => {
					if (!syn.connectedAt) {
						return;
					}

					this.wordBubbles.forEach(bubble => {
						if (syn.matchingWord === bubble.word) {
							const connectorPathEl = document.getElementById('matcher-synonym-path-' + syn.idx);
							const synEl = document.getElementById('matcher-synonym-word-' + syn.idx);
							const lineStart = getLineStartOutsideBBox(synEl, syn.x, syn.y, bubble.x, bubble.y);
							connectorPathEl.setAttribute('stroke', 'black');

							if (timeOffset < syn.connectedAt + CONNECTION_DURATION) {
								const fractionDone = (timeOffset - syn.connectedAt) / CONNECTION_DURATION;
								const xDist = (bubble.x - lineStart.x) * fractionDone;
								const yDist = (bubble.y - lineStart.y) * fractionDone;
								connectorPathEl.setAttribute('d', 'M ' + lineStart.x + ',' + lineStart.y + '\n' + 'l ' + xDist + ',' + yDist);
							} else {
								connectorPathEl.setAttribute('d', 'M ' + lineStart.x + ',' + lineStart.y + '\n' + 'L ' + bubble.x + ',' + bubble.y);
							}
						}
					});
				});

				// start connection of any matching words
				this.synonyms.forEach(syn => {
					if (syn.connectedAt) {
						return;
					}

					this.wordBubbles.forEach(bubble => {
						if (syn.matchingBubble === bubble && (syn === this.synonymMoving || !ONLY_CONNECT_MOVING_BUBBLE)) {
							const synEl = document.getElementById('matcher-synonym-bg-' + syn.idx);
							const synBBox = synEl.getBBox();
							const corners = getBBoxCorners(synBBox, true, bubble);
							const closest = pointClosestTo(bubble, corners);

							if (closest.distance <= CONNECTION_RANGE) {
								syn.connectedAt = timeOffset;
								syn.connectX = syn.x - bubble.x;
								syn.connectY = syn.y - bubble.y;
								syn.matchingBubble.xVel = 0;
								syn.matchingBubble.yVel = 0;
							}
						}
					});
				});
			},
			updateVelocities(elapsed) {
				// this.updateLineForces(elapsed);
				this.wordBubbles.forEach(bubble => {
					// slow down due to friction
					let frictionReduction = 1 - COEFFICIENT_OF_FRICTION * elapsed;
					if (frictionReduction > 1) {
						frictionReduction = 1;
					}
					bubble.xVel *= frictionReduction;
					bubble.yVel *= frictionReduction;

					this.updateAttractionRepulsion(bubble, elapsed);

					const vel = velocityFromXYVel(bubble);
					if (vel <= MIN_VELOCITY) {
						bubble.xVel = 0;
						bubble.yVel = 0;
					} else if (vel > MAX_VELOCITY) {
						const fractionToKeep = MAX_VELOCITY / vel;
						bubble.xVel *= fractionToKeep;
						bubble.yVel *= fractionToKeep;
					}
				});
			},
			updateAttractionRepulsion(bubble, elapsed) {
				this.synonyms.forEach(synonym => {
					if (synonym.connectedAt && synonym.matchingBubble === bubble) {
						return;
					}

					const synEl = document.getElementById('matcher-synonym-bg-' + synonym.idx);
					if (synEl) {
						const synBBox = synEl.getBBox();
						const synCorners = getBBoxCorners(synBBox, true, bubble);
						const closest = pointClosestTo(bubble, synCorners);

						const dist = Math.max(distance(bubble, closest.point), MIN_DIST);
						const GRAV_MAX = this.synonymMoving === synonym ? GRAVITY_MAX_RANGE_MOUSEDOWN : GRAVITY_MAX_RANGE;
						if (dist <= GRAV_MAX) {
							const numerator = bubble.mass * synonym.mass * GRAVITY_CONSTANT;
							const direction = directionFromXYVector(bubble.x - synonym.x, bubble.y - synonym.y);
							let xAcc;
							let yAcc;
							if (bubble === synonym.matchingBubble && dist >= RADIUS) {
								// attract
								const force = numerator / Math.pow(dist || 1, 2);
								const acceleration = force / bubble.mass;
								xAcc = Math.abs(Math.cos(direction) * acceleration);
								yAcc = Math.abs(Math.sin(direction) * acceleration);

								if (bubble.x > synonym.x) {
									xAcc *= -1;
								}

								if (bubble.y > synonym.y) {
									yAcc *= -1;
								}
							} else {
								// repel
								const force = dist > RADIUS ? numerator / Math.pow(dist || 1, 2) : numerator / BOUNCE_DENOMENATOR;
								const acceleration = force / bubble.mass;
								xAcc = Math.abs(Math.cos(direction) * acceleration);
								yAcc = Math.abs(Math.sin(direction) * acceleration);

								if (bubble.x < synonym.x) {
									xAcc *= -1;
								}

								if (bubble.y < synonym.y) {
									yAcc *= -1;
								}
							}

							bubble.xVel += xAcc * elapsed;
							bubble.yVel += yAcc * elapsed;
						}
					}
				});
			},
			moveAndHandleCollisions(elapsed) {
				this.wordBubbles.forEach(bubble => {
					handleOverlappingConnectedSynonyms(elapsed, bubble, this.synonyms);

					// bgEl should be pronounced "bagel"
					const bgEl = document.getElementById('matcher-bubble-bg-' + bubble.idx);
					const playerEl = document.getElementById('matcher-bubble-player-' + bubble.idx);
					const wordEl = document.getElementById('matcher-bubble-word-' + bubble.idx);

					if (!bgEl || !wordEl) {
						return;
					}

					handleBubbleToEdgeCollisions(elapsed, bubble, bgEl, this.synonyms);
					// handleBubbleToSynonymCollisions(elapsed, bubble, this.synonyms);
					handleBubbleCollisions(
						elapsed,
						bubble,
						this.wordBubbles,
						this.synonyms.filter(s => s !== this.synonymMoving)
					);

					bubble.x += bubble.xVel * elapsed;
					bubble.y += bubble.yVel * elapsed;

					bgEl.setAttribute('cx', bubble.x);
					bgEl.setAttribute('cy', bubble.y);
					wordEl.setAttribute('x', bubble.x);
					wordEl.setAttribute('y', bubble.y);
					playerEl.setAttribute('x', bubble.x + BUBBLE_PLAYER_X_OFFSET);
					playerEl.setAttribute('y', bubble.y + BUBBLE_PLAYER_Y_OFFSET);

					// move connected synonyms with the word bubble
					this.synonyms.forEach(syn => {
						if (syn.connectedAt && syn.matchingBubble === bubble) {
							syn.x = bubble.x + syn.connectX;
							syn.y = bubble.y + syn.connectY;
							const synEl = document.getElementById('matcher-synonym-word-' + syn.idx);
							if (!synEl) {
								return;
							}
							synEl.setAttribute('x', syn.x);
							synEl.setAttribute('y', syn.y);

							this.positionSynonymBackgrounds(syn);
						}
					});
				});
			},
			startMoving(ev, synonym) {
				ev.preventDefault();

				this.lastSynonymTouched = synonym;
				const evPos = eventPosition(ev);

				if (evPos && !synonym.connectedAt) {
					this.synonymMoving = synonym;
					this.synonymStart.x = synonym.x;
					this.synonymStart.y = synonym.y;
					this.movementStart.x = evPos.clientX;
					this.movementStart.y = evPos.clientY;
				}
			},
			moveSynonym(ev) {
				if (!this.$refs.svg) {
					return;
				}

				const evPos = eventPosition(ev);
				if (!evPos || this.synonymMoving.connectedAt) {
					return;
				}
				const xScale = VIEWBOX_WIDTH / this.$refs.svg.clientWidth;
				const yScale = VIEWBOX_HEIGHT / this.$refs.svg.clientHeight;

				const xDiff = this.movementStart.x - evPos.clientX;
				const yDiff = this.movementStart.y - evPos.clientY;

				const wordBgBBox = document.getElementById('matcher-synonym-bg-' + this.synonymMoving.idx).getBBox();

				const collisionXMinOffset = this.synonymMoving.x - wordBgBBox.x;
				const collisionYMinOffset = this.synonymMoving.y - wordBgBBox.y;
				const collisionXMaxOffset = wordBgBBox.x + wordBgBBox.width - this.synonymMoving.x;
				const collisionYMaxOffset = wordBgBBox.y + wordBgBBox.height - this.synonymMoving.y;

				this.synonymMoving.x = this.synonymStart.x - xDiff * xScale;
				if (this.synonymMoving.x - collisionXMinOffset < 0) {
					this.synonymMoving.x = collisionXMinOffset;
				}
				if (this.synonymMoving.x + collisionXMaxOffset > VIEWBOX_WIDTH) {
					this.synonymMoving.x = VIEWBOX_WIDTH - collisionXMaxOffset;
				}

				this.synonymMoving.y = this.synonymStart.y - yDiff * yScale;
				if (this.synonymMoving.y - collisionYMinOffset < 0) {
					this.synonymMoving.y = collisionYMinOffset;
				}
				if (this.synonymMoving.y + collisionYMaxOffset > VIEWBOX_HEIGHT) {
					this.synonymMoving.y = VIEWBOX_HEIGHT - collisionYMaxOffset;
				}

				const wordEl = document.getElementById('matcher-synonym-word-' + this.synonymMoving.idx);
				wordEl.setAttribute('x', this.synonymMoving.x);
				wordEl.setAttribute('y', this.synonymMoving.y);

				this.positionSynonymBackgrounds(this.synonymMoving);
			}
		}
	};
</script>
<template>
	<div
		class="vocab-matcher-activity-container"
		:class="{ printing: printing }">
		<header>
			<h3><span>2.</span> Word Matcher</h3>
			<p v-if="printing">
				<!-- prettier-ignore -->
				Every word has other words that have similar meanings or even the exact same meaning (these are called synonyms!). Draw a line from each similar word or synonym to the vocabulary word that it matches!
				<!-- prettier-ignore-end -->
			</p>
			<p v-else>
				Every word has other words that have similar meanings or even the exact same meaning (these are called synonyms!). Move each similar word or
				synonym to the vocabulary word that it matches! Listen to the similar words and synonyms by clicking on them and then click on the audio icon in
				the bottom left.
				<text-to-speech-player-simple
					v-if="showTts"
					text="Every word has other words that have similar meanings or even the exact same meaning (these are called synonyms!). Move each similar word or synonym to the vocabulary word that it matches! Listen to the similar words and synonyms by clicking on them and then click on the audio icon in the bottom left."
					:event-data="{
						aid: assignmentId,
						pid: activeProduct.id,
						tag: 'vocab_matcher_instructions'
					}">
				</text-to-speech-player-simple>
			</p>
		</header>
		<div
			class="print-body"
			v-if="printing">
			<div class="synonyms">
				<div
					v-for="syn in synonyms"
					:key="syn.word"
					class="synonym"
					:style="`flex-basis: ${synonymPrintWidth};`"
					v-text="syn.word"></div>
			</div>
			<div class="bubbles">
				<div
					v-for="bubble in wordBubbles"
					:key="bubble.word"
					class="bubble"
					v-html="bubble.word"></div>
			</div>
		</div>
		<div
			v-else
			class="vocab-activity vocab-matcher-activity"
			:class="{ completed: complete }">
			<h2 class="completion-header">Congratulations!</h2>
			<button
				v-if="complete"
				class="button -outline button-reset"
				@click="reset">
				Reset
			</button>
			<transition name="activity-svg">
				<svg
					ref="svg"
					viewBox="0 0 3200 1200"
					version="1.1"
					xmlns="http://www.w3.org/2000/svg"
					xmlns:xlink="http://www.w3.org/1999/xlink"
					v-show="!complete">
					<path
						v-for="(syn, synIndex) in synonyms"
						:key="`syn-path-${synIndex}`"
						:id="'matcher-synonym-path-' + synIndex"
						class="connector" />
					<g
						v-for="(bubble, bubbleIndex) in wordBubbles"
						:key="`bubble-word-${bubbleIndex}`"
						class="word">
						<circle
							:cx="bubble.x"
							:cy="bubble.y"
							:r="bubble.r"
							:id="'matcher-bubble-bg-' + bubbleIndex" />
						<text
							:x="bubble.x"
							:y="bubble.y"
							text-anchor="middle"
							:id="'matcher-bubble-word-' + bubbleIndex">
							{{ bubble.word }}
						</text>
						<foreignObject
							:x="bubble.x + BUBBLE_PLAYER_X_OFFSET"
							:y="bubble.y + BUBBLE_PLAYER_Y_OFFSET"
							width="70"
							height="70"
							:id="'matcher-bubble-player-' + bubbleIndex">
							<div
								xmlns="http://www.w3.org/1999/xhtml"
								class="player-in-bubble">
								<audio-player-simple
									v-if="showTts"
									reverse-color
									no-pause
									:url="product.wordAudioUrl(bubble.word)"
									:event-data="{ aid: workspace && workspace.aid, pid: activeProduct.id, word: bubble.word }"
									:audio-play-event-name="events.VOCAB_MATCHER_AUDIO_PLAY"
									:audio-pause-event-name="events.VOCAB_MATCHER_AUDIO_PAUSE"
									:audio-complete-event-name="events.VOCAB_MATCHER_AUDIO_COMPLETE">
								</audio-player-simple>
							</div>
						</foreignObject>
					</g>
					<g
						v-for="(syn, synIndex) in synonyms"
						:key="`syn-word-${synIndex}`"
						@mousedown="startMoving($event, syn)"
						@touchstart="startMoving($event, syn)">
						<rect
							class="synonym-bg"
							:id="'matcher-synonym-bg-' + synIndex"
							rx="20"></rect>
						<text
							:x="syn.x"
							:y="syn.y"
							:id="'matcher-synonym-word-' + synIndex"
							class="synonym-word"
							:class="{ 'synonym-word-connected': !!syn.connectedAt }"
							text-anchor="middle">
							{{ syn.word }}
						</text>
					</g>
				</svg>
			</transition>
			<div
				v-if="!complete && lastSynonymTouched && showTts"
				class="audio-wrapper">
				<span
					class="audio-word-tab"
					:style="{ width: lastSynonymTouchedWidth }">
					<span
						ref="lastSynonymTouched"
						class="audio-word">
						<text-to-speech-player-simple
							:text="lastSynonymTouched.word"
							:event-data="{
								aid: assignmentId,
								pid: activeProduct.id,
								tag: 'vocab_matcher_results_synonym'
							}">
						</text-to-speech-player-simple>
						{{ lastSynonymTouched.word }}
					</span>
				</span>
			</div>
			<transition name="complete-show-summary">
				<div
					v-show="complete"
					class="word-summary">
					<div
						v-for="entry in summaryEntries"
						:key="`entry-${entry.word}`">
						<h3>
							{{ entry.word }}
							<audio-player-simple
								v-if="showTts"
								no-pause
								:url="product.wordAudioUrl(entry.word)"
								:event-data="{ aid: workspace && workspace.aid, pid: activeProduct.id, word: entry.word }"
								:audio-play-event-name="events.VOCAB_MATCHER_AUDIO_PLAY"
								:audio-pause-event-name="events.VOCAB_MATCHER_AUDIO_PAUSE"
								:audio-complete-event-name="events.VOCAB_MATCHER_AUDIO_COMPLETE">
							</audio-player-simple>
						</h3>
						<div class="synonyms">
							synonyms:
							<ul>
								<li
									v-for="syn in entry.synonyms"
									:key="syn">
									{{ syn }}
									<text-to-speech-player-simple
										v-if="showTts"
										:text="syn"
										:event-data="{
											aid: assignmentId,
											pid: activeProduct.id,
											tag: 'vocab_matcher_results_synonym'
										}">
									</text-to-speech-player-simple>
								</li>
							</ul>
						</div>
					</div>
				</div>
			</transition>
			<vocab-powered-by-wordsmyth></vocab-powered-by-wordsmyth>
		</div>
	</div>
</template>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';

	.vocab-matcher-activity {
		h2 {
			text-align: center;
		}

		.player-in-bubble {
			position: relative;
			width: 100%;
			height: 100%;

			.audio-player-simple {
				display: flex;
				height: 100%;
				.audio-player-icon {
					flex: 1;
					height: 100%;
					img {
						height: 100%;
						width: 100%;
					}
				}
			}
		}

		.activity-svg-leave-active {
			transition:
				opacity 0.7s,
				transform 0.7s;
			opacity: 1;
		}

		.activity-svg-leave {
			opacity: 0;
			transform: translateY(-4rem);
		}

		.complete-show-summary-enter-to {
			opacity: 0.1;
			transform: translateY(5rem);
		}

		.complete-show-summary-enter-active {
			transition:
				opacity 0.7s,
				transform 0.7s;
		}

		.word-summary {
			background-color: white;
			display: flex;

			> div {
				h3 {
					color: $c-blue;
					margin: 1rem 0 0.5rem;
				}

				position: relative;
				padding-left: 8%;
				padding-top: 1rem;
				flex-grow: 1;
				min-height: 12rem;

				&:after {
					content: ' ';
					position: absolute;
					right: 0;
					top: 10%;
					height: 80%;
					width: 1px;
					background-color: $c-slate-dark;
				}

				.synonyms {
					position: relative;
					display: inline-block;

					ul {
						padding: 0 0.5rem 0 1.5rem;
						margin: 0;
						list-style: none;
						font-weight: bold;
					}
				}
			}

			> div:last-child {
				&:after {
					background-color: transparent;
				}
			}
		}

		svg {
			background-color: white;

			.word {
				circle {
					fill: $c-blue;
					stroke: $c-blue;
					stroke-width: 8;
				}

				text {
					stroke: white;
					font-size: 40pt;
					fill: white;
					cursor: default;
				}
			}

			.word.active {
				circle {
					fill: white;
				}
			}

			.synonym-bg {
				fill: white;
				stroke: $c-blue;
				stroke-width: 5px;
			}

			.synonym-word {
				font-size: 40pt;
				stroke: $c-blue;
				fill: $c-blue;
				cursor: default;
			}

			.synonym-word-connected {
				stroke: $c-blue;
				fill: $c-blue;
				font-weight: bold;
			}

			.connector {
				stroke: $c-blue;
				stroke-width: 4px;
			}
		}

		.audio-wrapper {
			position: relative;
			.audio-word-tab {
				.text-to-speech-player-simple {
					position: relative;
					top: 0.05rem;
				}
				position: absolute;
				left: 0;
				top: 0;
				white-space: nowrap;
				width: 1rem;
				height: 2rem;
				overflow: hidden;
				transition: width 200ms ease-in-out;
				color: $c-blue;
				background-color: white;
				border-bottom-left-radius: 0.4rem;
				border-bottom-right-radius: 0.4rem;
				padding: 6px 10px;
			}
		}
	}

	.vocab-matcher-activity-container {
		&.printing {
			.print-body {
				position: relative;
				margin: 1rem 0;
				padding: 4rem 0;
			}

			.synonyms {
				position: absolute;
				display: flex;
				justify-content: space-around;
				flex-wrap: wrap;
				align-content: space-between;
				width: 100%;
				height: 100%;
				top: 0;
			}

			.synonym {
				flex: 1 1;
				text-align: center;
			}

			.bubbles {
				display: flex;
				justify-content: space-around;
			}

			.bubble {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 6rem;
				height: 3rem;
				line-height: 1;
				border: 1px solid;
				border-radius: 50%;
				font-weight: 700;
			}
		}
	}
</style>
